import { memo, useContext, useState, useEffect } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";
import { getAbbreviatedDay } from "@utils/strings";
import { getHoursForDay } from "@api/businessService";
import { useFormContext } from "react-hook-form";
import { TimePicker } from "@components/forms/TimePicker";
import { Switch } from "@mui/joy";
import { dateFromTime, isValidDate } from "@utils/dates";
import { isDate } from "date-fns";

interface BaseColumn {
  label: string;
  class?: string;
}

interface ColumnWithValue extends BaseColumn {
  // label: string;
  value: string;//|number|null;
  // class?: string;
  transformer?: never;
} //& CommonColumn

interface ColumnWithTransformer extends BaseColumn {
  // label: string;
  value?: never;
  // class?: string;
  transformer: Function;
}

type IColumn = ColumnWithValue | ColumnWithTransformer;

const ObjectRelationshipHoursEdit = memo(function ObjectRelationshipHoursEdit({
  relationship = [],
  title,
  tint = null,
  sort = null,
  columns,
  collapsible = true,
}) {
  const { control, register, setValue, getValues, watch, setError, clearErrors } = useFormContext();
  const relationshipData = getValues()[relationship];
  const watchedData = watch(relationship); // Watch for changes
  const data = relationshipData ?? []; //watch(relationship) ?? [];
  // const isEmpty = !relationship;
  const [validationErrors, setValidationErrors] = useState<{[key: number]: string}>({});


  const isCompleteTime = (timeValue) => {
    console.log('isCompleteTime check:', timeValue, typeof timeValue);
    
    if (!timeValue || timeValue === null || timeValue === undefined || timeValue === '') {
      return false;
    }
    
    if (timeValue instanceof Date) {
      const valid = isValidDate(timeValue);
      console.log('Date object valid?', valid);
      return valid;
    }

    if (typeof timeValue === 'string') {

      const timePattern = /^\d{1,2}:\d{2}/;
      const matches = timePattern.test(timeValue);
      console.log('String pattern match?', matches, 'value:', timeValue);
      return matches;
    }
    
    console.log('Fell through - returning false');
    return false;
  };

  const isPartialTime = (timeValue) => {
    console.log('isPartialTime check:', timeValue, typeof timeValue);
    
    if (!timeValue || timeValue === null || timeValue === undefined || timeValue === '') {
      return false;
    }
    
    if (timeValue instanceof Date) {
      const invalid = !isValidDate(timeValue);
      console.log('Date is partial?', invalid);
      return invalid;
    }
    
    if (typeof timeValue === 'string') {
      const timePattern = /^\d{1,2}:\d{2}/;
      const isPartial = !timePattern.test(timeValue);
      console.log('String is partial?', isPartial, 'value:', timeValue);
      return isPartial;
    }
    
    console.log('Truthy but unknown type - treating as partial');
    return true;
  };


  const validateHours = (rowIndex: number) => {
    const row = data[rowIndex];
    console.log('=== VALIDATING ROW', rowIndex, '===');
    console.log('Row data:', row);

    if (row.is_closed) {
      console.log('Day is closed - clearing errors');
      setValidationErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[rowIndex];
        return newErrors;
      });
      clearErrors(`hours.${rowIndex}`);
      return true;
    }

    const openIsComplete = isCompleteTime(row.open_time);
    const closeIsComplete = isCompleteTime(row.close_time);
    const openIsPartial = isPartialTime(row.open_time);
    const closeIsPartial = isPartialTime(row.close_time);

    console.log('Validation results:', {
      openIsComplete,
      closeIsComplete,
      openIsPartial,
      closeIsPartial
    });

    if (openIsPartial) {
      const errorMsg = 'Opening time is incomplete. Please finish entering the time or clear it.';
      console.log('SETTING ERROR:', errorMsg);
      setValidationErrors(prev => ({
        ...prev,
        [rowIndex]: errorMsg
      }));
      setError(`hours.${rowIndex}`, {
        type: 'manual',
        message: errorMsg,
      });
      return false;
    }

    if (closeIsPartial) {
      const errorMsg = 'Closing time is incomplete. Please finish entering the time or clear it.';
      console.log('SETTING ERROR:', errorMsg);
      setValidationErrors(prev => ({
        ...prev,
        [rowIndex]: errorMsg
      }));
      setError(`hours.${rowIndex}`, {
        type: 'manual',
        message: errorMsg,
      });
      return false;
    }

    if (openIsComplete !== closeIsComplete) {
      const errorMsg = 'Both opening and closing times must be set, or both must be empty.';
      console.log('SETTING ERROR:', errorMsg);
      setValidationErrors(prev => ({
        ...prev,
        [rowIndex]: errorMsg
      }));
      setError(`hours.${rowIndex}`, {
        type: 'manual',
        message: errorMsg,
      });
      return false;
    }

    console.log('VALIDATION PASSED - clearing errors');
    setValidationErrors(prev => {
      const newErrors = {...prev};
      delete newErrors[rowIndex];
      return newErrors;
    });
    clearErrors(`hours.${rowIndex}`);
    return true;
  };

  useEffect(() => {
    if (data && data.length > 0) {
      data.forEach((_, index) => {
        validateHours(index);
      });
    }
  }, [watchedData]);

  const setRowValue = (rowIndex, key, value) => {
    data[rowIndex][key] = value;
    setValue(relationship, data, { shouldValidate: true });
    
    
    validateHours(rowIndex);
  };

  return (
    <ObjectRelationshipCard
      unpadded
      // empty={isEmpty}
      title={
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
          })}
        >
          <CardActions
            sx={theme => ({
              py: 1.5,
            })}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={0.5}
            >
              <Typography sx={theme => ({flex: 1, fontWeight: '600', color: 'white' })}>{title}</Typography>
            </Stack>
          </CardActions>
        </CardOverflow>
      }
      sx={{
        '--mm-card-height': 'auto',
      }}
    >
      {/* <CardOverflow> */}
      <Table
        // stickyHeader
        stripe="odd"
        // hoverRow
        sx={{
          margin: 0,
          padding: 0,
          tableLayout: 'auto',
          'tbody th': {
            backgroundColor: 'transparent',
          },
          /*
          'tbody td': {
            borderWidth: '0 !important',
          },
          */
        }}
      >
        <thead>
          <tr>
            <th>Day</th>
            <th>Open Time</th>
            <th>Close Time</th>
          </tr>
        </thead>
        <tbody>
        {data.map((row, rowIndex:number) => {
          const formData = relationshipData[rowIndex];
          const hasError = validationErrors[rowIndex];

          return (
          <>
          <tr key={rowIndex}>
            {/* <th scope="row" className="min">{getAbbreviatedDay(row.day)}</th> */}
            <th scope="row" className="min">
              <Typography sx={{userSelect: 'none'}} component="label" startDecorator={
                <Switch
                  variant="solid"
                  // {...field}
                  value={(!!formData?.is_closed)}
                  onChange={(e) => {
                    setRowValue(rowIndex, 'is_closed', formData?.is_closed ? 0 : 1)
                    // setValue(demographic.name, value ? 0 : 1);// !value)
                  }}
                  // checked={value}
                  checked={!formData?.is_closed}
                />
              }>
                {row.day}
              </Typography>
            </th>
            <td>
              <TimePicker
                disabled={!!formData?.is_closed}
                value={formData.open_time/* ? dateFromTime(formData.open_time) : null*/}
                onChange={(v) => {
                  if(v === null || isValidDate(v)) {
                    setRowValue(rowIndex, 'open_time', v)
                  }
                }}
              />
            </td>
            <td>
              <TimePicker
                disabled={!!formData?.is_closed}
                value={formData.close_time/* ? dateFromTime(formData.close_time) : null*/}
                onChange={(v) => {
                  if(v === null || isValidDate(v)) {
                    setRowValue(rowIndex, 'close_time', v)
                  }
                }}
              />
            </td>
            {/* <td>{getHoursForDay(row)}</td> */}
          </tr>
          {hasError && (
            <tr key={`error-${rowIndex}`}>
              <td colSpan={3}>
                <Typography level="body-sm" sx={{ color: 'danger.500', fontSize: 'xs', pl: 1 }}>
                  ⚠️ {hasError}
                </Typography>
              </td>
            </tr>
          )}
          </>
        )})}
        </tbody>
      </Table>

      <Typography sx={{
      textAlign: 'center',
      fontSize: 'sm',
      p: 1,
    }}>Note: Leave hours empty to set "by appointment only"</Typography>
      {/* </CardOverflow> */}

    </ObjectRelationshipCard>
  );
});

export default ObjectRelationshipHoursEdit;